/**
 * ForgeSwap Bridge Client
 *
 * Handles postMessage communication with the parent page for wallet operations
 * when the iframe can't directly access wallet injections (mobile in-app browser case).
 *
 * Protocol:
 * Request:  { type: "FORGESWAP_BRIDGE_REQUEST", id, wallet, method, params }
 * Response: { type: "FORGESWAP_BRIDGE_RESPONSE", id, success, result?, error? }
 */

export type WalletName = "phantom" | "solflare";

export type BridgeMethod =
  | "connect"
  | "disconnect"
  | "signTransaction"
  | "signAllTransactions"
  | "signMessage"
  | "ping";

export interface BridgeRequest {
  type: "FORGESWAP_BRIDGE_REQUEST";
  id: string;
  wallet: WalletName;
  method: BridgeMethod;
  params?: Record<string, unknown>;
}

export interface BridgeResponse {
  type: "FORGESWAP_BRIDGE_RESPONSE";
  id: string;
  success: boolean;
  result?: unknown;
  error?: string;
}

interface PendingRequest {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

const REQUEST_TIMEOUT_MS = 30000; // 30 seconds
const PING_TIMEOUT_MS = 2000; // 2 seconds - allows time for handler to lazy-load web3.js

// Hardcoded allowed parent origins (always permitted)
const HARDCODED_ORIGINS = [
  "https://forgepad.fun",
  "https://newrelease.forgepad.fun",
];

// Match any *.forgepad.fun subdomain
const FORGEPAD_SUBDOMAIN_PATTERN = /^https:\/\/[a-z0-9-]+\.forgepad\.fun$/;

// Extra origins from env var (comma-separated)
// Set NEXT_PUBLIC_BRIDGE_EXTRA_ORIGINS=https://example.com,https://test.com
// to allow additional parent origins (e.g., for staging/testing)
function getExtraOrigins(): string[] {
  if (typeof process === "undefined") return [];
  const envVar = process.env.NEXT_PUBLIC_BRIDGE_EXTRA_ORIGINS;
  if (!envVar) return [];
  return envVar
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.startsWith("https://") || s.startsWith("http://"));
}

function isAllowedOrigin(origin: string): boolean {
  if (HARDCODED_ORIGINS.includes(origin)) return true;
  if (FORGEPAD_SUBDOMAIN_PATTERN.test(origin)) return true;
  if (getExtraOrigins().includes(origin)) return true;
  return false;
}

export class BridgeClient {
  private pendingRequests = new Map<string, PendingRequest>();
  private verifiedParentOrigin: string | null = null; // Cached after first valid response
  private isListening = false;

  constructor() {
    this.handleMessage = this.handleMessage.bind(this);
  }

  /**
   * Start listening for bridge responses
   */
  startListening(): void {
    if (this.isListening) return;

    window.addEventListener("message", this.handleMessage);
    this.isListening = true;
    console.log("[ForgeSwap Bridge] Started listening for messages");
  }

  /**
   * Stop listening for bridge responses
   */
  stopListening(): void {
    if (!this.isListening) return;

    window.removeEventListener("message", this.handleMessage);
    this.isListening = false;

    // Reject all pending requests
    for (const [id, pending] of this.pendingRequests) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error("Bridge client stopped"));
      this.pendingRequests.delete(id);
    }

    console.log("[ForgeSwap Bridge] Stopped listening for messages");
  }

  /**
   * Handle incoming messages from parent
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin against allowlist
    if (!isAllowedOrigin(event.origin)) {
      // Silently ignore messages from non-allowed origins
      return;
    }

    // Validate message structure
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type !== "FORGESWAP_BRIDGE_RESPONSE") return;

    const response = data as BridgeResponse;

    // Validate we have a pending request for this ID
    const pending = this.pendingRequests.get(response.id);
    if (!pending) {
      console.warn("[ForgeSwap Bridge] Received response for unknown request:", response.id);
      return;
    }

    // Cache the verified parent origin for future targeted postMessages
    if (!this.verifiedParentOrigin) {
      this.verifiedParentOrigin = event.origin;
      console.log("[ForgeSwap Bridge] Verified parent origin:", this.verifiedParentOrigin);
    }

    // Clear timeout and remove from pending
    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(response.id);

    // Resolve or reject based on success
    if (response.success) {
      pending.resolve(response.result);
    } else {
      pending.reject(new Error(response.error || "Bridge request failed"));
    }
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for older browsers
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Send a request to the parent bridge
   */
  async sendRequest<T = unknown>(
    wallet: WalletName,
    method: BridgeMethod,
    params?: Record<string, unknown>,
    timeoutMs: number = REQUEST_TIMEOUT_MS
  ): Promise<T> {
    if (!this.isListening) {
      this.startListening();
    }

    // Can't send if not in iframe
    if (window === window.top) {
      throw new Error("Bridge can only be used inside an iframe");
    }

    const id = this.generateRequestId();

    const request: BridgeRequest = {
      type: "FORGESWAP_BRIDGE_REQUEST",
      id,
      wallet,
      method,
      params,
    };

    return new Promise<T>((resolve, reject) => {
      // Set up timeout
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Bridge request timed out after ${timeoutMs}ms: ${method}`));
      }, timeoutMs);

      // Store pending request
      this.pendingRequests.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId,
      });

      // Send to parent - use verified origin if known, "*" only for initial ping
      try {
        const targetOrigin = this.verifiedParentOrigin || "*";
        window.parent.postMessage(request, targetOrigin);
        console.log("[ForgeSwap Bridge] Sent request:", method, id, "to:", targetOrigin);
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingRequests.delete(id);
        reject(new Error(`Failed to send bridge request: ${error}`));
      }
    });
  }

  /**
   * Check if the bridge is available by sending a ping
   */
  async ping(): Promise<boolean> {
    try {
      const result = await this.sendRequest<{ pong: boolean }>(
        "phantom", // wallet doesn't matter for ping
        "ping",
        undefined,
        PING_TIMEOUT_MS
      );
      return result?.pong === true;
    } catch {
      return false;
    }
  }

  /**
   * Get the verified parent origin (for debugging)
   */
  getVerifiedParentOrigin(): string | null {
    return this.verifiedParentOrigin;
  }
}

// Singleton instance
let bridgeClientInstance: BridgeClient | null = null;

export function getBridgeClient(): BridgeClient {
  if (!bridgeClientInstance) {
    bridgeClientInstance = new BridgeClient();
  }
  return bridgeClientInstance;
}
